import React, { useState, useEffect } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Users, Plus, Edit, Trash2, Shield, User, Key, Crown } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminUsers() {
  const { user: currentUser, isAdmin, isOwner } = useOutletContext();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [roleInfo, setRoleInfo] = useState(null);
  const [showAddUser, setShowAddUser] = useState(false);
  const [showEditUser, setShowEditUser] = useState(null);
  const [showResetPassword, setShowResetPassword] = useState(null);
  const [showChangeOwnPassword, setShowChangeOwnPassword] = useState(false);
  const [newUser, setNewUser] = useState({ name: '', email: '', password: '', role: 'user' });
  const [editData, setEditData] = useState({ name: '', email: '', role: 'user', is_active: true });
  const [newPassword, setNewPassword] = useState('');
  const [ownPasswordData, setOwnPasswordData] = useState({ current_password: '', new_password: '', confirm_password: '' });

  useEffect(() => {
    if (!isAdmin) {
      toast.error('Admin access required');
      navigate('/admin/dashboard');
      return;
    }
    fetchUsers();
    fetchRoleInfo();
  }, [isAdmin]);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`, { withCredentials: true });
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const fetchRoleInfo = async () => {
    try {
      const response = await axios.get(`${API}/admin/me/role`, { withCredentials: true });
      setRoleInfo(response.data);
    } catch (error) {
      console.error('Failed to fetch role info');
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/users`, newUser, { withCredentials: true });
      toast.success('User created successfully');
      setShowAddUser(false);
      setNewUser({ name: '', email: '', password: '', role: 'user' });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleEditUser = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/admin/users/${showEditUser.user_id}`, editData, { withCredentials: true });
      toast.success('User updated successfully');
      setShowEditUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`, { withCredentials: true });
      toast.success('User deleted');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    try {
      await axios.put(
        `${API}/admin/users/${showResetPassword.user_id}/password`,
        { password: newPassword },
        { withCredentials: true }
      );
      toast.success('Password reset successfully');
      setShowResetPassword(null);
      setNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    }
  };

  const handleChangeOwnPassword = async (e) => {
    e.preventDefault();
    if (ownPasswordData.new_password !== ownPasswordData.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }
    try {
      await axios.put(
        `${API}/admin/me/password`,
        { current_password: ownPasswordData.current_password, new_password: ownPasswordData.new_password },
        { withCredentials: true }
      );
      toast.success('Password changed successfully');
      setShowChangeOwnPassword(false);
      setOwnPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    }
  };

  const openEditModal = (user) => {
    setEditData({
      name: user.name,
      email: user.email,
      role: user.role || 'user',
      is_active: user.is_active !== false
    });
    setShowEditUser(user);
  };

  const canEditUser = (targetUser) => {
    if (isOwner) return true;
    // Admins can only edit users, not other admins or owner
    return targetUser.role === 'user';
  };

  const canResetPassword = (targetUser) => {
    // Self - use change own password
    if (targetUser.user_id === currentUser?.user_id) return false;
    // Owner can reset admin passwords
    if (isOwner) return targetUser.role !== 'owner';
    // Admin can only reset user passwords
    return targetUser.role === 'user';
  };

  const canDeleteUser = (targetUser) => {
    if (targetUser.user_id === currentUser?.user_id) return false;
    if (targetUser.role === 'owner') return false;
    if (isOwner) return true;
    // Admin can only delete users
    return targetUser.role === 'user';
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'owner': return <Crown size={12} />;
      case 'admin': return <Shield size={12} />;
      default: return <User size={12} />;
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'owner': return 'bg-purple/10 text-purple';
      case 'admin': return 'bg-burgundy/10 text-burgundy';
      default: return 'bg-gold/10 text-gold';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="admin-users-page">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-serif text-charcoal">User Management</h1>
          <p className="text-charcoal/60">
            {isOwner ? 'Owner access: Manage all users and admins' : 'Admin access: Manage content users'}
          </p>
        </div>
        <div className="flex gap-3">
          <Button
            onClick={() => setShowChangeOwnPassword(true)}
            variant="outline"
            className="border-gold/20"
            data-testid="change-own-password-btn"
          >
            <Key size={18} className="mr-2" /> Change My Password
          </Button>
          <Button
            onClick={() => setShowAddUser(true)}
            className="bg-burgundy hover:bg-purple text-white"
            data-testid="add-user-btn"
          >
            <Plus size={18} className="mr-2" /> Add User
          </Button>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gold/10 overflow-hidden">
        <table className="w-full">
          <thead className="bg-cream">
            <tr>
              <th className="text-left px-6 py-4 text-sm font-medium text-charcoal">User</th>
              <th className="text-left px-6 py-4 text-sm font-medium text-charcoal">Email</th>
              <th className="text-left px-6 py-4 text-sm font-medium text-charcoal">Role</th>
              <th className="text-left px-6 py-4 text-sm font-medium text-charcoal">Status</th>
              <th className="text-right px-6 py-4 text-sm font-medium text-charcoal">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gold/10">
            {users.map((u) => (
              <tr key={u.user_id} className="hover:bg-cream/50" data-testid={`user-row-${u.user_id}`}>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    {u.picture ? (
                      <img src={u.picture} alt={u.name} className="w-10 h-10 rounded-full" />
                    ) : (
                      <div className="w-10 h-10 bg-gold/20 rounded-full flex items-center justify-center">
                        <span className="text-charcoal font-medium">{u.name?.charAt(0)}</span>
                      </div>
                    )}
                    <div>
                      <p className="font-medium text-charcoal">{u.name}</p>
                      {u.user_id === currentUser?.user_id && (
                        <span className="text-xs text-gold">(You)</span>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 text-charcoal/70">{u.email}</td>
                <td className="px-6 py-4">
                  <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getRoleColor(u.role)}`}>
                    {getRoleIcon(u.role)}
                    {u.role === 'owner' ? 'Owner' : u.role === 'admin' ? 'Admin' : 'User'}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${
                    u.is_active !== false ? 'bg-olive/10 text-olive' : 'bg-red-100 text-red-600'
                  }`}>
                    {u.is_active !== false ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-6 py-4 text-right">
                  <div className="flex items-center justify-end gap-2">
                    {canEditUser(u) && (
                      <button
                        onClick={() => openEditModal(u)}
                        className="p-2 text-charcoal/50 hover:text-burgundy transition-colors"
                        title="Edit user"
                        data-testid={`edit-user-${u.user_id}`}
                      >
                        <Edit size={16} />
                      </button>
                    )}
                    {canResetPassword(u) && u.auth_type === 'email' && (
                      <button
                        onClick={() => setShowResetPassword(u)}
                        className="p-2 text-charcoal/50 hover:text-gold transition-colors"
                        title="Reset password"
                        data-testid={`reset-pwd-${u.user_id}`}
                      >
                        <Key size={16} />
                      </button>
                    )}
                    {canDeleteUser(u) && (
                      <button
                        onClick={() => handleDeleteUser(u.user_id)}
                        className="p-2 text-charcoal/50 hover:text-red-500 transition-colors"
                        title="Delete user"
                        data-testid={`delete-user-${u.user_id}`}
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Role Explanation */}
      <div className="mt-8 grid md:grid-cols-3 gap-4">
        <div className="bg-gold/10 rounded-xl p-6">
          <div className="flex items-center gap-2 mb-3">
            <User size={20} className="text-gold" />
            <h3 className="font-medium text-charcoal">User (Content Manager)</h3>
          </div>
          <ul className="text-sm text-charcoal/70 space-y-1">
            <li>• Manage all content</li>
            <li>• Respond to messages & prayers</li>
            <li>• View basic analytics</li>
            <li>• Cannot see financial data</li>
          </ul>
        </div>
        <div className="bg-burgundy/10 rounded-xl p-6">
          <div className="flex items-center gap-2 mb-3">
            <Shield size={20} className="text-burgundy" />
            <h3 className="font-medium text-charcoal">Admin</h3>
          </div>
          <ul className="text-sm text-charcoal/70 space-y-1">
            <li>• Everything Users can do</li>
            <li>• View donations & finances</li>
            <li>• Create & manage Users</li>
            <li>• Reset User passwords</li>
          </ul>
        </div>
        <div className="bg-purple/10 rounded-xl p-6">
          <div className="flex items-center gap-2 mb-3">
            <Crown size={20} className="text-purple" />
            <h3 className="font-medium text-charcoal">Owner</h3>
          </div>
          <ul className="text-sm text-charcoal/70 space-y-1">
            <li>• Everything Admins can do</li>
            <li>• Create & manage Admins</li>
            <li>• Reset Admin passwords</li>
            <li>• Full system access</li>
          </ul>
        </div>
      </div>

      {/* Add User Dialog */}
      <Dialog open={showAddUser} onOpenChange={setShowAddUser}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Add New User</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddUser} className="space-y-4 mt-4">
            <div>
              <Label>Full Name</Label>
              <Input
                value={newUser.name}
                onChange={(e) => setNewUser({...newUser, name: e.target.value})}
                required
                data-testid="new-user-name"
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                required
                data-testid="new-user-email"
              />
            </div>
            <div>
              <Label>Password</Label>
              <Input
                type="password"
                value={newUser.password}
                onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                required
                minLength={6}
                data-testid="new-user-password"
              />
            </div>
            <div>
              <Label>Role</Label>
              <Select value={newUser.role} onValueChange={(v) => setNewUser({...newUser, role: v})}>
                <SelectTrigger data-testid="new-user-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">User (Content Manager)</SelectItem>
                  {isOwner && <SelectItem value="admin">Admin (Full Access)</SelectItem>}
                </SelectContent>
              </Select>
              {!isOwner && <p className="text-xs text-charcoal/50 mt-1">Only Owner can create Admin accounts</p>}
            </div>
            <Button type="submit" className="w-full bg-burgundy hover:bg-purple" data-testid="submit-new-user">
              Create User
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={!!showEditUser} onOpenChange={() => setShowEditUser(null)}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Edit User</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditUser} className="space-y-4 mt-4">
            <div>
              <Label>Full Name</Label>
              <Input
                value={editData.name}
                onChange={(e) => setEditData({...editData, name: e.target.value})}
                required
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={editData.email}
                onChange={(e) => setEditData({...editData, email: e.target.value})}
                required
              />
            </div>
            <div>
              <Label>Role</Label>
              <Select value={editData.role} onValueChange={(v) => setEditData({...editData, role: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">User (Content Manager)</SelectItem>
                  {isOwner && <SelectItem value="admin">Admin (Full Access)</SelectItem>}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_active"
                checked={editData.is_active}
                onChange={(e) => setEditData({...editData, is_active: e.target.checked})}
                className="rounded"
              />
              <Label htmlFor="is_active" className="cursor-pointer">Account Active</Label>
            </div>
            <Button type="submit" className="w-full bg-burgundy hover:bg-purple">
              Save Changes
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={!!showResetPassword} onOpenChange={() => setShowResetPassword(null)}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Reset Password</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleResetPassword} className="space-y-4 mt-4">
            <p className="text-charcoal/70">
              Reset password for <strong>{showResetPassword?.name}</strong>
            </p>
            <div>
              <Label>New Password</Label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={6}
                placeholder="Minimum 6 characters"
              />
            </div>
            <Button type="submit" className="w-full bg-burgundy hover:bg-purple">
              Reset Password
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Change Own Password Dialog */}
      <Dialog open={showChangeOwnPassword} onOpenChange={setShowChangeOwnPassword}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Change Your Password</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleChangeOwnPassword} className="space-y-4 mt-4">
            <div>
              <Label>Current Password</Label>
              <Input
                type="password"
                value={ownPasswordData.current_password}
                onChange={(e) => setOwnPasswordData({...ownPasswordData, current_password: e.target.value})}
                required
              />
            </div>
            <div>
              <Label>New Password</Label>
              <Input
                type="password"
                value={ownPasswordData.new_password}
                onChange={(e) => setOwnPasswordData({...ownPasswordData, new_password: e.target.value})}
                required
                minLength={6}
              />
            </div>
            <div>
              <Label>Confirm New Password</Label>
              <Input
                type="password"
                value={ownPasswordData.confirm_password}
                onChange={(e) => setOwnPasswordData({...ownPasswordData, confirm_password: e.target.value})}
                required
                minLength={6}
              />
            </div>
            <Button type="submit" className="w-full bg-burgundy hover:bg-purple">
              Change Password
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
